/**
 * Crucible #1777 — translate the cloud runtime's `agent.send@1`
 * streaming chunks into the `agent:stream` push payload the shared
 * Workspace chat already consumes.
 *
 * `BrowserCrucibleClient.invoke('agent:send', …)` routes the turn over
 * `invokeStreaming` (the streaming chunks are `agent.send.chunk@1`
 * notifications correlated by `params.requestId`), then bridges each
 * chunk through this translator into the existing `agent:stream`
 * subscribers — so `Workspace.tsx`'s chunk handler renders the cloud
 * turn (text, tool chips, terminal finalisation) WITHOUT the call site
 * changing. This is the `agent:send` twin of the Lane A 1c (#1314)
 * `game:create` → `game:create:progress` bridge.
 *
 * The runtime chunk shape is `{chunkKind, ...emitter-params}` — pinned
 * by `runtime/jsonrpc-methods/agent-send-emitter-adapter.ts`
 * (`METHOD_TO_CHUNK_KIND`). The seven kinds the Workspace consumes:
 *
 *     agent/text         → "text"
 *     agent/tool_call    → "tool_call"
 *     agent/tool_result  → "tool_result"
 *     agent/done         → "done"
 *     agent/error        → "error"
 *     agent/warning      → "warning"
 *     agent/interrupted  → "interrupted"
 *
 * The translation mirrors the desktop GUI's cloud dispatch port
 * (`crucible-gui/src/main/cloud-agent-dispatcher.ts:translateWireChunk`)
 * field-for-field so the chat reducer sees an identical chunk shape
 * regardless of which surface drove the turn. Unknown chunkKinds
 * (`file_edit`, future pod-side extensions) are dropped silently —
 * the same defence-in-depth posture as the desktop translator.
 *
 * The `AgentStreamChunk` push payload shape comes from
 * `@volley/crucible-ipc-types` — the type the `agent:stream` entry in
 * `PushChannelMap` already pins.
 */

import type { AgentStreamChunk } from '@volley/crucible-ipc-types';

/**
 * Mirror of the runtime's originator wire chunk for `agent.send@1`
 * (the `agent.send.chunk@1` notification `params`, minus the
 * `requestId`/`seq` envelope fields the core strips). Re-declared here
 * because `@volley/crucible-client` does not depend on
 * `@volley/crucible` (the runtime package that owns the emitter
 * param types); the streaming notifications arrive as `params`
 * objects of this shape.
 */
export interface AgentSendWireChunk {
  chunkKind: string;
  dispatchOutcome?: 'started' | 'not-needed';
  /** Runtime-session id — NOT the GUI's `gui-<gameId>-<ts>` stream id.
   *  The bridge re-stamps every translated chunk with the GUI-shaped
   *  id so the Workspace's cross-game discriminator accepts it. */
  sessionId?: string;
  /** text — the production emitter-adapter emits `delta`; the runtime's
   *  default DEMO dispatcher emits `text`. The runtime's own observer
   *  translator (`translateOriginatorChunkToObserver`) accepts either,
   *  `text` winning when both are present — this bridge mirrors that
   *  (Codex P1 on #1780). */
  text?: string;
  delta?: string;
  index?: number;
  /** tool_call */
  toolName?: string;
  toolInput?: unknown;
  /** tool_call + tool_result */
  callId?: string;
  /** tool_result */
  result?: unknown;
  isError?: boolean;
  /** error / warning / interrupted */
  message?: string;
  code?: string;
  retriable?: boolean;
  source?: string;
  /** done */
  finalText?: string;
  usage?: unknown;
  /** done + error */
  totalCostUsd?: number;
  numTurns?: number;
  /** subagent attribution (text / tool_call / tool_result / warning) */
  agentId?: string;
  sourceMessageUuid?: string;
  retractedMessageUuids?: string[];
}

/**
 * The terminal `agent:stream` chunk types — rendering one of these is
 * the Workspace chat reducer's single source of truth for "the turn
 * is over" (flips `isAiTyping` false, finalises the bubble, drains the
 * queued user message). Mirrors
 * `crucible-gui/src/main/agent-stream-terminal.ts:isAgentStreamTerminalChunk`.
 */
export function isAgentStreamTerminalChunk(chunk: AgentStreamChunk): boolean {
  return chunk.type === 'error' || chunk.type === 'done' || chunk.type === 'interrupted';
}

/**
 * Translate ONE `agent.send@1` streaming wire chunk into the
 * `agent:stream` push payload, or `null` for chunkKinds the Workspace
 * does not consume (`file_edit`, future pod-side extensions —
 * dropped silently, matching the desktop translator).
 *
 * `sessionId` is the GUI-shaped stream id (`gui-<gameId>-<ts>`) the
 * bridge mints per dispatch; every translated chunk is stamped with it
 * so the Workspace's cross-game discriminator
 * (`^gui-<gameId>-\d+$`, Workspace.tsx) accepts the chunk. The wire
 * chunk's own `sessionId` is the runtime's protocol session id, which
 * that regex would silently drop — the exact evaporation #1777 fixes.
 */
export function agentSendChunkToStream(
  raw: AgentSendWireChunk,
  sessionId: string,
): AgentStreamChunk | null {
  switch (raw.chunkKind) {
    case 'text': {
      // Accept BOTH text-chunk shapes: the production emitter-adapter's
      // `delta` AND the demo dispatcher's `text` (`text` wins when both
      // are present — runtime observer-translator parity; Codex P1).
      const text =
        typeof raw.text === 'string' ? raw.text : typeof raw.delta === 'string' ? raw.delta : '';
      const chunk: AgentStreamChunk = {
        type: 'text',
        sessionId,
        text,
      };
      if (typeof raw.agentId === 'string') chunk.agentId = raw.agentId;
      if (typeof raw.sourceMessageUuid === 'string') {
        chunk.sourceMessageUuid = raw.sourceMessageUuid;
      }
      return chunk;
    }
    case 'tool_call': {
      // Defensive shape-coercion mirrors the desktop translator: the
      // wire's toolInput is `unknown`; the renderer's contract is a
      // Record, so non-object inputs ride through under `{ value }`.
      const toolInput =
        raw.toolInput !== null && typeof raw.toolInput === 'object' && !Array.isArray(raw.toolInput)
          ? (raw.toolInput as Record<string, unknown>)
          : { value: raw.toolInput };
      const chunk: AgentStreamChunk = {
        type: 'tool_call',
        sessionId,
        toolCall: {
          id: typeof raw.callId === 'string' ? raw.callId : '',
          name: typeof raw.toolName === 'string' ? raw.toolName : '',
          input: toolInput,
        },
      };
      if (typeof raw.agentId === 'string') chunk.agentId = raw.agentId;
      if (typeof raw.sourceMessageUuid === 'string') {
        chunk.sourceMessageUuid = raw.sourceMessageUuid;
      }
      return chunk;
    }
    case 'tool_result': {
      // The wire `result` is unknown; coerce to the renderer's
      // string-shaped output (same collapse the desktop translator
      // and the local SDK adapter apply).
      let output: string;
      if (typeof raw.result === 'string') {
        output = raw.result;
      } else if (raw.result === undefined || raw.result === null) {
        output = '';
      } else {
        try {
          output = JSON.stringify(raw.result);
        } catch {
          output = String(raw.result);
        }
      }
      const chunk: AgentStreamChunk = {
        type: 'tool_result',
        sessionId,
        toolResult: {
          id: typeof raw.callId === 'string' ? raw.callId : '',
          // Tool name is not echoed on the wire's tool_result chunk
          // (correlation is by callId). Empty name; the renderer's
          // reducer keys by id. Desktop-translator parity.
          name: '',
          output,
          isError: raw.isError === true,
        },
      };
      if (typeof raw.agentId === 'string') chunk.agentId = raw.agentId;
      if (typeof raw.sourceMessageUuid === 'string') {
        chunk.sourceMessageUuid = raw.sourceMessageUuid;
      }
      return chunk;
    }
    case 'warning': {
      const chunk: AgentStreamChunk = {
        type: 'warning',
        sessionId,
        error: typeof raw.message === 'string' ? raw.message : '',
      };
      if (typeof raw.agentId === 'string') chunk.agentId = raw.agentId;
      return chunk;
    }
    case 'error': {
      // #1724 coordination note: the error payload stays the wire
      // chunk's `message` string verbatim — the same contract the
      // desktop translator ships today. When #1724 lands its
      // CrucibleError envelope on error chunks, BOTH translators move
      // together.
      const chunk: AgentStreamChunk = {
        type: 'error',
        sessionId,
        error: typeof raw.message === 'string' ? raw.message : 'unknown',
      };
      if (typeof raw.totalCostUsd === 'number') {
        chunk.totalCostUsd = raw.totalCostUsd;
      }
      if (typeof raw.numTurns === 'number') {
        chunk.numTurns = raw.numTurns;
      }
      return chunk;
    }
    case 'interrupted':
      return { type: 'interrupted', sessionId };
    case 'message_retraction':
      return {
        type: 'message_retraction',
        sessionId,
        retractedMessageUuids: Array.isArray(raw.retractedMessageUuids)
          ? raw.retractedMessageUuids.filter((uuid): uuid is string => typeof uuid === 'string')
          : [],
      };
    case 'done': {
      const chunk: AgentStreamChunk = { type: 'done', sessionId };
      if (typeof raw.totalCostUsd === 'number') {
        chunk.totalCostUsd = raw.totalCostUsd;
      }
      if (typeof raw.numTurns === 'number') {
        chunk.numTurns = raw.numTurns;
      }
      return chunk;
    }
    default:
      // Unknown chunkKind (`file_edit` today — the renderer's
      // AgentStreamChunkType has no such member — or a future
      // pod-side extension). Drop silently; desktop-translator parity.
      return null;
  }
}
